import angular from 'angular';
import tzlookup from 'tz-lookup';
import { debounce, cloneDeep, includes, pull, get, isUndefined, set, isNumber } from 'lodash';
import {
  localStorage,
  options,
  containsEmptyElements,
  removeEmptyItems,
  optionsTotemVersion,
} from '../../utils';
import '../../services/globals';
import '../../services/timezone';
import '../../services/user';
import '../../services/auth';
import '../../services/language';
import '../../services/location';
import '../../services/line-flow-type-organization';
import '../../services/place';
import '../../services/errors';
import '../../services/category';
import '../../services/country';
import '../../services/place-messages';
import '../../services/sla';
import '../../services/organization-sla';
import Roles from '../../enums/roles';

const module = angular.module('whyline.modals.place', [
  'ngMap',
  'whyline.services.globals',
  'whyline.services.timezone',
  'whyline.services.user',
  'whyline.services.auth',
  'whyline.services.language',
  'whyline.services.location',
  'whyline.services.line-flow-type-organization',
  'whyline.services.place',
  'whyline.services.errors',
  'whyline.services.category',
  'whyline.services.country',
  'whyline.directives.focusme',
  'whyline.services.place-messages',
  'whyline.services.sla',
  'whyline.services.organization-sla',
]);

const PlaceModalController = (
  // Imports
  $scope,
  $uibModalInstance,
  $translate,
  $timeout,
  Slug,
  dialog,
  // Resolve
  placeId,
  organization,
  // Services
  UserService,
  LocationService,
  LanguageService,
  TimezoneService,
  PlaceService,
  AuthService,
  NotificationService,
  ErrorService,
  CategoryService,
  CountryService,
  LineFlowTypeOrganizationService,
  PlaceMessagesService,
  API_URL,
  Upload,
  $http,
  $rootScope,
  SlaService,
  OrganizationSlaService,
) => {
  'ngInject';

  $scope.loaded = false;

  /**
   *  As HTML version of input type email doesn't allow ñ character
   *  we added a specific regexp to validate email and name fields.
   *
   * @type {RegExp}
   */
  $scope.validEmail = /^[_a-zA-Z0-9ñÑ]+(\.[_a-zA-Z0-9ñÑ]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*(\.[a-zA-Z]{2,5})$/;
  $scope.minValidName = /^[ a-zA-Z0-9_.+-ñÑ]*(?:[a-zA-ZñÑ][ a-zA-Z0-9_.+-ñÑ]*){2,}$/;

  $scope.urlPattern = /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;
  $scope.blockSaveButton = false;
  $scope.editCallLogicsScope = {
    options: options.filter((o) => o.onlyPlace || o.placeLines),
    selectedOption: options.filter((o) => o.onlyPlace || o.placeLines)[0].key,
  };
  $scope.slaListEnabled = [];
  $scope.searchFields = [];
  $scope.selectedDefaultIdentification = {
    value: undefined,
  };
  const user = localStorage.get('user');
  $scope.superAdmin = user && user.sudo;
  $scope.enterpriseType = organization.configuration.queuer.enterpriseType;
  $scope.showButtonShowPeopleAheadAfterCheckIn =
    organization.configuration.mobileApp.showPeopleAheadAfterCheckIn;
  $scope.placeMessages = [];

  $scope.totemVersion = optionsTotemVersion;
  $scope.totemVersionChange = (totemVersionSelected) => {
    $scope.place.configuration.currentTotemVersion = totemVersionSelected.version;
  };

  const roleName = get(user, 'permissions[0].role.name', '');
  $scope.canEditExtraAppointment = $scope.superAdmin || roleName === Roles.organizationAdmin;

  const permissions = get(user, 'permissions', '');
  const roleAdminGeneral = Roles.adminGeneral;
  $scope.admin = permissions.some(
    (permission) => roleAdminGeneral === permission.role.name && permission.placeId === placeId,
  );

  $scope.canEditAgingSLA =
    organization.configuration.agingSLA.enabled &&
    ($scope.superAdmin || roleName === Roles.organizationAdmin);

  const addEmptyIdentification = (identifications) => {
    if (identifications.find((id) => id.empty)) {
      return;
    }

    const valEmpty = () => false;
    const empty = {
      empty: true,
      inputType: 'text',
      key: '',
      name: $translate.instant('select_option'),
      show: true,
      validator: valEmpty.toString(),
    };

    identifications.unshift(empty);
  };

  const setAndGetIdSlaSelected = () => {
    const foundSla = $scope.slaListEnabled.find(
      (sla) => sla._id === $scope.place.configuration.agingSLA._id,
    );
    $scope.slaSelected = foundSla ?? $scope.slaListEnabled[0];

    return $scope.slaSelected._id;
  };

  $scope.onPlanChanged = () => {
    $scope.place.plan.name = '';
  };

  $scope.setDefaultIndentificationsList = () => {
    const currentIdentifications = $scope.place.requirements.identifications;
    $scope.availableDefaultIdentifications = currentIdentifications.filter((id) => id.show);
    $scope.selectedDefaultIdentification.value =
      currentIdentifications.find((id) => id.default) ||
      currentIdentifications.find((id) => id.empty);
  };

  $scope.updateDefaultIdentification = () => {
    const defaultKey = get($scope.selectedDefaultIdentification, 'value.key', undefined);

    const identifications = $scope.place.requirements.identifications;

    identifications.forEach((id) => {
      id.default = false;
      return id;
    });

    if (!defaultKey) {
      return;
    }

    const defaultId = identifications.find((id) => id.key === defaultKey);

    if (defaultId) {
      defaultId.default = true;
    }
  };

  // Init
  $scope.forms = {};
  $scope.uploadmetadata = null;
  $scope.status = {
    user: {
      isOpen: !placeId,
      isDisabled: !!placeId,
    },
    location: {
      isOpen: !!placeId,
      isDisabled: !placeId,
    },
    information: {
      isOpen: false,
      isDisabled: true,
      shouldNameBeFocused: false,
    },
    configuration: {
      isOpen: false,
      isDisabled: !placeId,
    },
    messages: {
      isOpen: false,
      isDisabled: true,
    },
    metadata: {
      isOpen: false,
      isDisabled: false,
    },
    noPlace: false,
    showTooltip: false,
  };

  const uploadMetdataFile = (aPlaceId) => {
    if (!aPlaceId) {
      NotificationService.Warning($translate.instant('save_before_continue'));
      return Promise.resolve();
    }

    const {
      place: {
        metadata: { delimiter },
      },
      uploadmetadata,
    } = $scope;

    if (uploadmetadata) {
      const configuration = {
        url: `${API_URL}metadata/${aPlaceId}?delimiter=${delimiter}`,
        method: 'POST',
        file: uploadmetadata,
      };

      return Upload.upload(configuration)
        .then(() => {
          NotificationService.Success($translate.instant('metadata_uploaded_ok'));
        })
        .catch((err) => {
          console.log('uploadMetadataFile =>', err);

          NotificationService.Error($translate.instant('metadata_uploaded_error'));
        });
    } else {
      return Promise.resolve();
    }
  };

  $scope.deleteMetadata = () => {
    let n = name || '';
    dialog.confirm($translate.instant('metadata_remove_sure').replace('{name}', n)).then(() => {
      const {
        place: { _id },
      } = $scope;

      const url = `${API_URL}metadata/${_id}`;

      $http
        .delete(url)
        .then(() => {
          NotificationService.Success($translate.instant('metadata_deleted_ok'));
        })
        .catch((err) => {
          console.log('deleteMetadata =>', err);

          NotificationService.Error($translate.instant('metadata_deleted_error'));
        });
    });
  };

  $scope.removeMetadataFile = () => {
    $scope.uploadmetadata = null;
  };

  $scope.uploadMetadataFile = async (file) => {
    $scope.uploadmetadata = file;
  };

  $scope.languages = {
    list: {
      en: 'English',
      es: 'Español',
      pt: 'Portugués',
    },
  };

  const SUPPORTED_LANGUAGES = ['en', 'es', 'pt'];
  const DEFAULT_LANGUAGE = 'en';
  const language = includes(SUPPORTED_LANGUAGES, $translate.use())
    ? $translate.use()
    : DEFAULT_LANGUAGE;

  const field = `name_${language}`;

  $scope.categoriesSettings = {
    template: `<b>{{option.${field}}}</b>`,
    externalIdProp: 'category',
    displayProp: field,
    idProp: '_id',
    searchField: field,
    enableSearch: true,
    keyboardControls: true,
    selectedToTop: true,
    selectionLimit: 3,
    scrollable: true,
  };

  $scope.multiselectTranslations = {
    checkAll: $translate.instant('multiselect_todos'),
    uncheckAll: $translate.instant('multiselect_ninguno'),
    dynamicButtonTextSuffix: $translate.instant('multiselect_seleccionados'),
    buttonDefaultText: $translate.instant('multiselect_seleccionar'),
    searchPlaceholder: $translate.instant('multiselect_buscar'),
    selectionCount: $translate.instant('multiselect_seleccionados'),
  };
  $scope.userExists = true;
  $scope.canComplete = false;

  const enabledForMobileApp = organization.enabledForMobileApp || true;
  const hiddenInMobileApp = organization.hiddenInMobileApp || false;

  // gets the value from the org settings
  const { showInApp = false, showInKiosk = false } = organization.configuration.showCommercialOffer;
  const showCommercialOfferDefault = { showInApp, showInKiosk };

  const emptyPlace = {
    name: '',
    language: LanguageService.GetBrowserLanguage(),
    categories: organization.categories,
    timezone: '',
    branchId: '',
    enabledForMobileApp,
    hiddenInMobileApp,
    configuration: {
      currentTotemVersion: optionsTotemVersion[0].version,
      noAppointmentsAvailableMessage: organization.configuration.noAppointmentsAvailableMessage,
      aliasType: organization.configuration.queuer.aliasType,
      aliasGetter: organization.configuration.queuer.aliasGetter,
      appointmentReminder: {
        enabled: get(organization, 'configuration.appointmentReminder.enabled', false),
      },
      extraAppointment: {
        enabled: get(organization, 'configuration.extraAppointment.enabled', false),
        quantity: get(organization, 'configuration.extraAppointment.quantity', 1),
      },
      boxLabelRequired: organization.configuration.queuer.boxLabelRequired,
      canConfirmAppointments: organization.configuration.queuer.canConfirmAppointments,
      canConfirmSpontaneous: false,
      canEditAppointments: organization.configuration.queuer.canEditAppointments,
      canGiveAppointments: organization.configuration.queuer.canGiveAppointments,
      validateClosingHours: false,
      viewEasyTotem: false,
      viewNewTotem: true,
      searchAppointmentsBy: 'id',
      showCommercialOffer: showCommercialOfferDefault,
      user: organization.configuration.user,
      showPriorityTotem: false,
      canViewTotemToggles: false,
      checkIn: {
        min: organization.configuration.checkIn.min,
        max: organization.configuration.checkIn.max,
      },
      requireAppointmentConfirmation:
        organization.configuration.queuer.requireAppointmentConfirmation,
      canProcessAppointment: organization.configuration.queuer.canProcessAppointment,
      canSendAppointmentDelayAlert:
        organization.configuration.mobileApp.canSendAppointmentDelayAlert || false,
      checkInTolerance: organization.configuration.queuer.checkInTolerance || 2,
      closedMessage: organization.configuration.queuer.closedMessage,
      reasonRequired: organization.configuration.reasonRequired,
      reasons: organization.configuration.reasons,
      canSkipProcessOrder: organization.configuration.queuer.canSkipProcessOrder,
      requeue: organization.configuration.queuer.requeue,
      requestReasonAndMotive: organization.configuration.requestReasonAndMotive,
      retained: organization.configuration.queuer.retained,
      showWaitingInLineInPlace: organization.configuration.queuer.showWaitingInLineInPlace,
      showWorkingDay: organization.configuration.queuer.showWorkingDay,
      multiLanguageKiosk: false,
      sla: 0,
      agingSLA: {
        _id: get(organization, 'configuration.agingSLA._id', ''),
        enabled: get(organization, 'configuration.agingSLA.enabled', false),
      },
    },
    requirements: organization.configuration.requirements,
    location: {},
    info: {
      website: '',
      phones: [],
      address: '',
      city: '',
      state: '',
      country: '',
      zipCode: '',
      street: '',
      streetNumber: '',
    },
    openingDetails: {},
    geoLocation: [],
    checkInCode: '',
    mobileAppConfig: {
      showPeopleAheadAfterCheckIn: true,
      showCheckInButton: true,
      showWaitingTime: true,
      showQRCode: false,
    },
    userAdmin: {
      local: {
        email: '',
        password: '',
      },
      profile: {
        firstName: '',
        lastName: '',
      },
    },
    metadata: {
      enabled: false,
      delimiter: ';',
      fields: {
        mapsText: '',
        maps: [],
        identifier: '',
        email: '',
        phone: '',
      },
    },
  };
  $scope.lineFlowTypeOrganizationList = [];
  $scope.lineFlowTypeOrganizationSelected = {};

  $scope.setLFTOId = (lftoSelected) => {
    $scope.place.defaultLineFlowTypeOrganization = lftoSelected._id;
  };

  $scope.slaSelected = {};
  $scope.setSlaId = (slaSelected) => {
    $scope.place.configuration.agingSLA._id = slaSelected._id;
  };

  $scope.defaultAppointmentConfirmation = () => {
    if (
      !$scope.place.configuration.canConfirmAppointments ||
      !$scope.place.configuration.canGiveAppointments
    ) {
      $scope.place.configuration.requireAppointmentConfirmation = false;
    }
    if (!$scope.place.configuration.canGiveAppointments) {
      $scope.place.configuration.showWorkingDay = false;
    }
    if (!$scope.place.configuration.canGiveAppointments) {
      $scope.place.configuration.canProcessAppointment = false;
    }
    if ($scope.place.configuration.canGiveAppointments) {
      $scope.place.configuration.canConfirmAppointments = true;
    }
  };

  $scope.negateToggles = () => {
    if (!$scope.place.configuration.viewNewTotem) {
      $scope.place.configuration.multiLanguageKiosk = false;
      $scope.place.configuration.showPriorityTotem = false;
    }
  };

  // Data for angular wizard
  const categoriesPromise = CategoryService.GetAllAsPromise();
  const aliasTypesPromise = PlaceService.GetAliasTypes();

  const slaListPromise = placeId
    ? SlaService.GetAllEnabled(placeId)
    : OrganizationSlaService.GetAllEnabled(organization._id);

  Promise.all([categoriesPromise, aliasTypesPromise, slaListPromise]).then(
    ([categories, aliasTypes, slaList]) => {
      $scope.data = {
        confirmPassword: '',
        phone: '',
        locationAsString: '',
        address: '',
        virtualineImage: '',
        mobileAppImage: '',
        results: [],
        addressResults: [],
        selectedCategory: [],
        categories,
        aliasTypes,
        selectedPlace: '',
        timezones: TimezoneService.List,
        addressSearch: '',
        selectedAddress: '',
      };
      const fields = ['id', 'phoneNumber', 'email'];
      $scope.slaListEnabled = slaList;
      if (placeId) {
        PlaceService.GetOneAsPromise(placeId)
          .then((place) => {
            $timeout(async () => {
              $scope.place = PlaceService.Copy(place);
              if ($scope.place.configuration.currentTotemVersion) {
                $scope.totemVersionSelected = optionsTotemVersion.filter(ver => ver.version === $scope.place.configuration.currentTotemVersion)[0];
              } else {
                $scope.totemVersionSelected = optionsTotemVersion[0];
                $scope.place.configuration.currentTotemVersion = optionsTotemVersion[0].version;
              }
              for (const field of fields) {
                let req = $scope.place.requirements[field];
                if (req !== undefined) {
                  $scope.searchFields.push({name: field, enabled: req})
                } else {
                  req = $scope.place.requirements.virtualine[field]
                  req !== undefined ? $scope.searchFields.push({name: field, enabled: req}) : null;
                }
              }
              $scope.searchFields.push({name: 'name', enabled: true});
              const currentSearchField = $scope.searchFields.find(field => field.name === $scope.place.configuration.searchAppointmentsBy);
              if (!currentSearchField.enabled) {
                const enabledSearchFields = $scope.searchFields.filter(field => field.enabled);
                $scope.place.configuration.searchAppointmentsBy = enabledSearchFields[0].name;
                NotificationService.Warning($translate.instant('searchField_changed'));
              }
              $scope.data.selectedPlace = PlaceService.Copy(place);
              $scope.data.phone = $scope.place.info.phones[0] || '';
              $scope.status.showTooltip = true;
              $scope.data.locationAsString = [place.location.lat, place.location.lng];
              $scope.data.address = $scope.place.info.address;

              $scope.place.metadata = $scope.place.metadata || {
                ...emptyPlace.metadata,
              };

              setMetadataMapTextFromMaps();

              PlaceMessagesService.GetAllAsPromise(placeId)
                .then((messages) => {
                  $timeout(() => {
                    $scope.placeMessages = messages;
                  });
                })
                .catch(ErrorService.handler);

              // Set categories
              $scope.place.categories.forEach((category) => {
                let c = $scope.data.categories.find((cate) => category == cate.key);
                if (c) {
                  $scope.data.selectedCategory.push(c);
                }
              });

              // eslint-disable-next-line no-use-before-define
              await setCountryAndIdentifications($scope.place);

              LineFlowTypeOrganizationService.GetAllEnabledByPlace(placeId, true).then(
                (lftoList) => {
                  $scope.lineFlowTypeOrganizationList = lftoList;
                  const found = $scope.lineFlowTypeOrganizationList.find(
                    (lfto) => lfto._id === $scope.place.defaultLineFlowTypeOrganization,
                  );
                  $scope.lineFlowTypeOrganizationSelected =
                    found || $scope.lineFlowTypeOrganizationList[0];
                },
              );

              $scope.place.configuration.agingSLA._id = setAndGetIdSlaSelected();
            });
          })
          .catch(ErrorService.handler);
      } else {
        $scope.place = cloneDeep(emptyPlace);

        if (organization.configuration.currentTotemVersion) {
          const selectTotemVersion = optionsTotemVersion.filter(ver => ver.version === organization.configuration.currentTotemVersion)[0];
          $scope.totemVersionSelected = selectTotemVersion;
          $scope.place.configuration.currentTotemVersion = selectTotemVersion.version;
        } else {
          $scope.totemVersionSelected = optionsTotemVersion[0];
          $scope.place.configuration.currentTotemVersion = optionsTotemVersion[0].version;
        }

        for (const field of fields) {
          let req = $scope.place.requirements[field];
          if (req !== undefined) {
            $scope.searchFields.push({name: field, enabled: req})
          } else {
            req = $scope.place.requirements.virtualine[field]
            req !== undefined ? $scope.searchFields.push({name: field, enabled: req}) : null;
          }
        }
        $scope.searchFields.push({name: 'name', enabled: true});
        LineFlowTypeOrganizationService.GetAllEnabledByOrganization(organization._id, true).then(
          (lftoList) => {
            $scope.lineFlowTypeOrganizationList = lftoList;
            const found = $scope.lineFlowTypeOrganizationList.find(
              (lfto) => lfto._id == organization.defaultLineFlowTypeOrganization,
            );
            $scope.lineFlowTypeOrganizationSelected = found
              ? found
              : $scope.lineFlowTypeOrganizationList[0];
            $scope.place.defaultLineFlowTypeOrganization =
              $scope.lineFlowTypeOrganizationSelected._id;
            emptyPlace.defaultLineFlowTypeOrganization = $scope.place.defaultLineFlowTypeOrganization;
          },
        );
        $scope.place.categories.forEach((category) => {
          let c = $scope.data.categories.find((cate) => category == cate.key);
          if (c) {
            $scope.data.selectedCategory.push(c);
          }
        });

        setAndGetIdSlaSelected();
      }
      $scope.loaded = true;
    },
  );

  function someIdentificationChecked() {
    const identifications = $scope.place.requirements.identifications
      .filter((id) => isUndefined(id.empty))
      .filter((identification) => identification.show);

    return identifications.length;
  }

  function noIdentificationChecked() {
    return !someIdentificationChecked();
  }

  const validate = () => {
    const name = $scope.place.name;
    const address = $scope.place.info.address;
    const timezone = $scope.place.timezone;
    const language = $scope.place.language;
    const checkInTolerance = $scope.place.configuration.checkInTolerance;
    const retained = $scope.place.configuration.retained;
    const requirements = $scope.place.requirements;
    const city = $scope.place.info.city;
    const state = $scope.place.info.state;
    const zipCode = $scope.place.info.zipCode;
    const country = $scope.place.info.country;
    const street = $scope.place.info.street;
    const streetNumber = $scope.place.info.streetNumber;
    const lineFlowTypeOrganization = $scope.place.defaultLineFlowTypeOrganization;
    const sla = $scope.place.configuration.sla;
    const inactivityMinutes = get($scope.place.configuration, 'inactivityAlert.minutes', 0);
    const checkDurationCompleteAction = get($scope, 'place.configuration.checkDurationCompleteAction', {});
    const inactivityMails = get($scope.place.configuration,'inactivityAlert.mails', ''); 
    const inactivityEnabled = get($scope.place.configuration,'inactivityAlert.enabled', false);
    const listEmailsPattern = /^(([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5}){1,25})+([,](([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5}){1,25})+)*$/;
    const noAppointmentsAvailableMessage = $scope.place.configuration.noAppointmentsAvailableMessage;
    const extraAppointmentQuantity = get($scope.place,'configuration.extraAppointment.quantity',0);
    
    if (get(checkDurationCompleteAction, 'enabled', false)) {
      const showMessageBeforeSeconds = get(checkDurationCompleteAction, 'showMessageBeforeSeconds', undefined);
      if (isNaN(showMessageBeforeSeconds) ||
        showMessageBeforeSeconds < 1 ||
        showMessageBeforeSeconds > 300 ||
        showMessageBeforeSeconds.toString().indexOf('.') != -1 ||
        showMessageBeforeSeconds.toString().indexOf(',') != -1) {
        NotificationService.Warning($translate.instant('place.show_message_before_seconds_range'));
        return false;
      }
    }

    const {
      metadata,
      metadata: { fields },
    } = $scope.place;

    if (metadata.enabled) {
      if (!fields.identifier && !fields.email && !fields.phone) {
        NotificationService.Warning($translate.instant('mandatory_metadata_identifier'));
        return false;
      }
    }

    const reasons = $scope.place.configuration.reasons.filter((r) => (r || '').trim().length > 0);
   
    if(inactivityEnabled && (!inactivityMails || !listEmailsPattern.test(inactivityMails))){
      NotificationService.Warning($translate.instant('enter_mails'));
      return false;
    }
    if (inactivityEnabled && (!inactivityMinutes || inactivityMinutes <= 0)) {
      NotificationService.Warning($translate.instant('enter_minutes'));
      return false;
    }

    if (!name) {
      NotificationService.Warning($translate.instant('mandatory_place_name'));
      return false;
    }

    if (inactivityMinutes > 60) {
      NotificationService.Warning($translate.instant('inactivity_alert_minutes_error'));
      return false;
    }

    if (
      name &&
      !/^[a-zA-Zàáâäãåąč&ćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽð:/ ,.'-+\d.*-]+$/u.test(
        name,
      )
    ) {
      NotificationService.Warning($translate.instant('invalid_place_name'));
      return false;
    }

    if (name.length > 100) {
      NotificationService.Warning($translate.instant('place_name_too_long'));
      return false;
    }

    if (!language) {
      NotificationService.Warning($translate.instant('mandatory_lang_place'));
      return false;
    }

    if (!timezone) {
      NotificationService.Warning($translate.instant('mandatory_timezone_place'));
      return false;
    }

    $scope.place.categories = $scope.data.selectedCategory.map((category) => category.key);

    if (!$scope.place.categories.length) {
      NotificationService.Warning($translate.instant('business_without_categories_error'));
      return false;
    }

    if (!city) {
      NotificationService.Warning($translate.instant('mandatory_city_place'));
      return false;
    }

    if (!state) {
      NotificationService.Warning($translate.instant('mandatory_state_place'));
      return false;
    }

    if (!zipCode) {
      NotificationService.Warning($translate.instant('mandatory_zip_place'));
      return false;
    }

    if (!country) {
      NotificationService.Warning($translate.instant('mandatory_country_place'));
      return false;
    }

    if (!address || !street || !streetNumber) {
      NotificationService.Warning($translate.instant('mandatory_address_place'));
      return false;
    }

    if (
      !checkInTolerance ||
      checkInTolerance > 100 ||
      checkInTolerance < 2 ||
      checkInTolerance.toString().indexOf('.') != -1 ||
      checkInTolerance.toString().indexOf(',') != -1
    ) {
      NotificationService.Warning($translate.instant('place_checkin_tolerance_range'));
      return false;
    }

    if (retained.enabled && (retained.days <= 0 || retained.days === undefined)) {
      NotificationService.Warning($translate.instant('retained_days_error'));
      return false;
    }

    if (sla < 0 || sla > 1000 || isUndefined(sla)) {
      NotificationService.Warning($translate.instant('sla_minutes_error'));
      return false;
    }

    if (noIdentificationChecked()) {
      NotificationService.Warning($translate.instant('mandatory_at_least_one_id'));
      return false;
    }

    if (!requirements.identifications.find((id) => id.default)) {
      NotificationService.Warning($translate.instant('mandatory_at_least_one_default_id'));
      return false;
    }

    if ($scope.place.configuration.requestReasonAndMotive && reasons.length === 0) {
      NotificationService.Warning($translate.instant('mandatory_reasons'));
      return false;
    }

    if (!lineFlowTypeOrganization) {
      NotificationService.Warning($translate.instant('mandatory_call_logic'));
      return false;
    }

    if (noAppointmentsAvailableMessage !== undefined && noAppointmentsAvailableMessage.length > 150) {
      NotificationService.Warning($translate.instant('noAppointments_message_too_long'));
      return false;
    }

    if( !isNumber(extraAppointmentQuantity) || extraAppointmentQuantity > 15 || extraAppointmentQuantity < 1){
      NotificationService.Warning($translate.instant('extra_appointment_max_min'));
      return false;
    }

    return true;
  };

  $scope.validateSpontaneousAndCheckinButton = (prop) => {
    if (prop === 'canConfirmSpontaneous') {
      if (
        !$scope.place.configuration.canConfirmSpontaneous &&
        !$scope.place.mobileAppConfig.showCheckInButton
      ) {
        $scope.place.mobileAppConfig.showCheckInButton = true;
        NotificationService.Information($translate.instant('set_showCheckInButton'), {
          timeOut: 10000,
        });
      }
    }
    if (prop === 'showCheckInButton') {
      if (
        !$scope.place.mobileAppConfig.showCheckInButton &&
        !$scope.place.configuration.canConfirmSpontaneous
      ) {
        $scope.place.configuration.canConfirmSpontaneous = true;
        NotificationService.Information($translate.instant('set_canConfirmSpontaneous'), {
          timeOut: 10000,
        });
      }
    }
  };

  const setMetadataMapTextFromMaps = () => {
    const {
      place: { metadata },
    } = $scope;

    metadata.fields.mapText = '';

    if (metadata.enabled) {
      metadata.fields.mapText = (metadata.fields.maps || []).join(';').toUpperCase();
    }
  };

  const setMetadataMapsFromMapText = () => {
    const {
      place: { metadata },
    } = $scope;

    metadata.fields.maps = [];

    if (metadata.enabled) {
      const mapText = (metadata.fields.mapText || '').trim();
      metadata.fields.maps = mapText !== '' ? mapText.toUpperCase().split(';') : [];
      metadata.fields.mapText = null;
      Reflect.deleteProperty($scope.place.metadata.fields, 'mapText');
    }
  };

  const removeEmptyReasons = () => {
    $scope.place.configuration.reasons = removeEmptyItems($scope.place.configuration.reasons);
  };

  const save = () => {
    $scope.place.userAdmin.local.password = AuthService.Encrypt(
      $scope.place.userAdmin.local.password,
    );
    removeEmptyReasons();
    return PlaceService.Create($scope.place);
  };

  const update = () => {
    Reflect.deleteProperty($scope.place, 'userAdmin');
    removeEmptyReasons();
    return PlaceService.UpdateWithCallLogic(
      $scope.place,
      $scope.editCallLogicsScope.selectedOption,
    );
  };

  const saveOrUpdate = () => {
    const isUpdate = !!placeId;

    const saveOrUpdatePromise = isUpdate ? update() : save();
    const mesageSucc = isUpdate ? 'changes_saved' : 'stablish_succ';
    return saveOrUpdatePromise
      .then((result) => {
        NotificationService.Success($translate.instant(mesageSucc));

        uploadMetdataFile(result._id).then(angular.noop);

        $rootScope.$broadcast('placeSaved', $scope.place);

        $uibModalInstance.close($scope.place);
      })
      .catch((error) => {
        ErrorService.handler(error);
        $scope.blockSaveButton = false;

        setMetadataMapTextFromMaps();
      });
  };

  const preSave = () => {
    $scope.blockSaveButton = true;

    $scope.place.info.phones[0] = $scope.data.phone;
    if (!$scope.place.branchId) {
      Reflect.deleteProperty($scope.place, 'branchId');
    }

    if (!$scope.place.checkInCode) {
      Reflect.deleteProperty($scope.place, 'checkInCode');
    }

    if (!$scope.place.configuration.requestReasonAndMotive) {
      $scope.place.configuration.reasons = [];
    } else {
      $scope.place.configuration.reasons = $scope.place.configuration.reasons.filter(
        (r) => (r || '').trim().length > 0,
      );
    }

    setMetadataMapsFromMapText();
  };

  $scope.save = () => {
    if (validate()) {
      preSave();

      return saveOrUpdate();
    }
  };

  $scope.cancel = () => {
    dialog
      .confirm($translate.instant('modal_cancel'))
      .then(() => $uibModalInstance.dismiss('cancel'))
      .catch(() => 'canceled');
  };

  $scope.checkAliasType = () => {
    $scope.requirementChange('id');
    if (!$scope.place.requirements.id) {
      $scope.place.configuration.aliasType = 'fullNameAlias';
      NotificationService.Information($translate.instant('alias_has_been_changed'));
    }
  };

  $scope.requirementChange = (type) => {
    $timeout(() => {
      const field = $scope.searchFields.find(field => field.name === type);
      $scope.searchFields[$scope.searchFields.indexOf(field)].enabled = type === 'email' ?  $scope.place.requirements.virtualine[type] : $scope.place.requirements[type];
      if ($scope.place.configuration.searchAppointmentsBy === type && !$scope.searchFields[$scope.searchFields.indexOf(field)].enabled) {
        const enabledSearchFields = $scope.searchFields.filter(field => field.enabled);
        $scope.place.configuration.searchAppointmentsBy = enabledSearchFields[0].name;
        NotificationService.Warning($translate.instant('searchField_changed'));
      }
    });
  }

  // Location
  const findByStablishment = () => {
    if (
      $scope.data &&
      $scope.data.address &&
      $scope.data.address.length >= 3 &&
      !$scope.data.selectedPlace
    ) {
      return LocationService.FindStablishments($scope.data.address).then((result) => {
        $timeout(() => {
          $scope.data.results = result;
        });
      });
    }
  };

  const findByAddress = () => {
    let search = $scope.data ? $scope.data.addressSearch : null;
    if (search && search.length >= 3 && !$scope.data.selectedAddress) {
      return LocationService.FindAddresses(search).then((result) => {
        $timeout(() => {
          $scope.data.addressResults = result;
        });
      });
    }
  };

  $scope.selectPlace = (place) => {
    if (place.place_id) {
      // Set description on search input
      if (place.description) {
        $scope.data.address = place.description;
      }

      // Get the details of the place selected by user
      LocationService.GetPlaceDetails(place.place_id).then((placeDetails) => {
        $timeout(async () => {
          $scope.data.selectedPlace = placeDetails;
          $scope.data.locationAsString = [
            placeDetails.geometry.location.lat(),
            placeDetails.geometry.location.lng(),
          ];
          $scope.place.location = {
            lat: placeDetails.geometry.location.lat(),
            lng: placeDetails.geometry.location.lng(),
          };
          await autocompleteFields(placeDetails);
        });
      });
    }

    $scope.data.results = [];
  };

  $scope.requestReasonAndMotiveChange = (value) => {
    if (!value) {
      $scope.place.configuration.reasonRequired = false;
    }
  };

  $scope.userBoxChange = (value) => {
    if (value) {
      $scope.place.configuration.boxLabelRequired = false;
    }
  };

  $scope.adminBoxChange = (value) => {
    if (value) {
      $scope.place.configuration.user.useBoxLabelByPace.enabled = false;
      $scope.place.configuration.user.useBoxLabelByPace.required = false;
    }
  };

  $scope.selectAddress = (address) => {
    const googlePlaceId = address.place_id;
    if (googlePlaceId) {
      // Set description on search input
      if (address.description) {
        $scope.data.addressSearch = address.description;
      }

      // Get the details of the place selected by user
      LocationService.GetPlaceDetails(googlePlaceId).then((placeDetails) => {
        $timeout(async () => {
          $scope.data.selectedAddress = placeDetails;
          $scope.place.location = {
            lat: placeDetails.geometry.location.lat(),
            lng: placeDetails.geometry.location.lng(),
          };
          await autocompleteFields(placeDetails, true);
        });
      });
    }
    $scope.data.addressResults = [];

    $timeout(async () => await setCountryAndIdentifications($scope.place));
  };

  $scope.$watch('data.address', debounce(findByStablishment, 500));
  $scope.$watch('data.addressSearch', debounce(findByAddress, 500));

  // Place info
  /* eslint-disable camelcase */
  async function autocompleteFields(placeDetails, excludeName) {
    const { name, website, formatted_phone_number, formatted_address, place_id } = placeDetails;

    if (name && !excludeName) {
      $scope.place.name = name;
    }

    $scope.place.info.website = website ? website : organization.website;
    $scope.data.phone = formatted_phone_number ? formatted_phone_number : organization.phoneNumber;

    if (formatted_address) {
      $scope.place.info.address = formatted_address;
    }

    if (placeDetails.opening_hours) {
      $scope.place.openingDetails.periods = placeDetails.opening_hours.periods;
      $scope.place.openingDetails.weekday_text = placeDetails.opening_hours.weekday_text;
    }

    // Google place id
    $scope.place.googlePlaceId = place_id;
    /* eslint-enable camelcase */

    // Check in code
    $scope.place.checkInCode = `${Slug.slugify(placeDetails.name)}_check_in`;

    // Address components
    Object.assign($scope.place.info, LocationService.GetLocationInfo(placeDetails));

    // Timezone
    $scope.place.timezone = tzlookup(
      placeDetails.geometry.location.lat(),
      placeDetails.geometry.location.lng(),
    );

    await setCountryAndIdentifications($scope.place);
  }

  $scope.restartSelectedPlace = () => {
    $scope.data.results = [];
    $scope.data.selectedPlace = '';
    $scope.data.address = '';
    $scope.data.locationAsString = '';
    $scope.data.addressSearch = '';
    $scope.data.addressResults = [];
    $scope.data.selectedAddress = '';

    if (!placeId) {
      let userAdmin = $scope.place.userAdmin;
      $scope.place = cloneDeep(emptyPlace);
      $scope.place.userAdmin = userAdmin;
      $scope.status = {
        ...$scope.status,
        location: {
          isOpen: true,
          isDisabled: false,
        },
        information: {
          isOpen: false,
          isDisabled: true,
          shouldNameBeFocused: false,
        },
        configuration: {
          isOpen: false,
          isDisabled: true,
        },
      };
    }
  };

  $scope.checkIfUserExists = (email) => {
    UserService.CheckIfExists(email).then((res) => {
      $timeout(() => {
        const user = res[0];
        if (user) {
          $scope.userExists = true;
          $scope.place.userAdmin = {
            _id: user._id,
            local: {
              email: user.local.email,
            },
            profile: {
              firstName: user.profile.firstName,
              lastName: user.profile.lastName,
            },
          };
          NotificationService.Information('El usuario existe y se ha autocompletado.');
        } else {
          $scope.userExists = false;
          Reflect.deleteProperty($scope.place.userAdmin, '_id');
          NotificationService.Information(
            'El usuario no existe, por favor completá los siguientes campos.',
          );
        }
        $scope.canComplete = true;
      });
    });
  };

  $scope.addReason = () => {
    const reasons = get($scope.place, 'configuration.reasons', []);
    if (containsEmptyElements(reasons)) {
      NotificationService.Information($translate.instant('reason_empty'));
      return false;
    }
    $scope.place.configuration.reasons.unshift('');
  };

  $scope.deleteReason = (reason) => {
    dialog
      .confirm($translate.instant('reason_remove_sure'))
      .then(() => {
        pull($scope.place.configuration.reasons, reason);
        NotificationService.Information($translate.instant('reason_remove_succ'));
      })
      .catch(ErrorService.handler);
  };

  $scope.emailChanged = () => {
    if ($scope.canComplete) {
      $scope.place.userAdmin = {
        local: {
          email: '',
          password: '',
        },
        profile: {
          firstName: '',
          lastName: '',
        },
      };
      $scope.userExists = true;
      $scope.canComplete = false;
    }
  };

  $scope.checkStep = {
    user: () => {
      if ($scope.forms.userPlace.$valid && !placeId) {
        $scope.status = {
          ...$scope.status,
          user: {
            isOpen: false,
          },
          location: {
            isOpen: true,
            isDisabled: false,
          },
        };
      }
    },
    location: () => {
      if ($scope.data.selectedPlace || $scope.status.noPlace) {
        $scope.status = {
          ...$scope.status,
          location: {
            isOpen: false,
            isDisabled: false,
          },
          information: {
            isOpen: true,
            isDisabled: false,
            shouldNameBeFocused: true,
          },
          configuration: {
            isDisabled: true,
          },
        };
      }
      if (placeId) {
        $scope.status.showTooltip = false;
      }
    },
    information: () => {
      if ($scope.forms.informationPlace.$valid) {
        $scope.status = {
          ...$scope.status,
          information: {
            isOpen: false,
            isDisabled: false,
            shouldNameBeFocused: false,
          },
          configuration: {
            isOpen: true,
            isDisabled: false,
          },
        };
      }
      if (placeId) {
        $scope.status.showTooltip = true;
        $scope.status.information.isDisabled = true;
      }
    },
  };

  async function setCountryAndIdentifications(place) {
    const location = (await LocationService.Find(place.info.address))[0];

    const countryFound = await CountryService.FindByCode(location.code);

    const country =
      countryFound ||
      (await CountryService.Create({
        code: location.code,
        name: location.country,
      }));

    $scope.place.location.country = country._id;
    $scope.place.location.countryCode = country.code;
    $scope.country = country.name;
    $scope.place.requirements.identifications = mergeCountryIdentifications(
      country.identifications,
      get(place, 'requirements.identifications', []),
    );

    $scope.placeIdentifications = [...$scope.place.requirements.identifications];

    addEmptyIdentification($scope.place.requirements.identifications);

    $scope.setDefaultIndentificationsList();
  }

  function mergeCountryIdentifications(countryIdentifications, placeIdentifications) {
    return countryIdentifications.map((identification) => {
      identification.show = identification.selected;
      Reflect.deleteProperty(identification, 'selected');
      return placeIdentificationOrDefault(placeIdentifications, identification);
    });
  }

  function placeIdentificationOrDefault(placeIdentifications, identification) {
    const placeIdentification = placeIdentifications.find((p) => p.key === identification.key);
    return Object.assign({}, identification, placeIdentification);
  }

  $scope.autoCheckInChange = (value) => {
    $scope.place.mobileAppConfig.showCheckInButton = !value;
    if (value) {
      $scope.place.mobileAppConfig.showQRCode = false;
      $scope.place.configuration.canConfirmSpontaneous = false;
    }
  };

  $scope.refreshCallLogic = () => {
    PlaceService.RefreshCallLogic(placeId)
      .then(() => NotificationService.Success('call_logic_reseted'))
      .catch(ErrorService.handler);
  };

  $scope.isDynamicAlias = () => {
    const dynamicAlias = 'dynamicAlias';
    const aliasType = get($scope, 'place.configuration.aliasType');
    let aliasGetter = get($scope, 'place.configuration.aliasGetter');
    const isDinamycAlias = aliasType === dynamicAlias;

    if (isDinamycAlias && !aliasGetter) {
      const fun = `
/* context = {_, subscription} 
  * _ = lodash
  * virtualine subscription 
  */
(function() { return _.get(subscription, '_id', 'default-value')}());
      `;

      const isDynamicAliasOnOrganization =
        get(organization, 'configuration.queuer.aliasType', null) === dynamicAlias;

      aliasGetter = isDynamicAliasOnOrganization
        ? get(organization, 'configuration.queuer.aliasGetter', fun)
        : fun;

      $timeout(() => {
        set($scope, 'place.configuration.aliasGetter', aliasGetter);
      });
    }

    return isDinamycAlias;
  };

  $scope.checkInKeyPress = e => {
    if (e.which !== 8 && e.which !== 0 && (e.which < 48 || e.which > 57)) {
      e.preventDefault();
    }
  };
};

module.exports = module.controller('PlaceModalController', PlaceModalController);
